/**
 * RENDA-K の試聴エンジン。
 *
 * 連打フレーズは最大 1 小節 + 前後 1 小節の試聴 window に収まるため、
 * 先読みスケジューラは使わず PUSH 時に全ソースを一括スケジュールする。
 * AudioContext は初回 PUSH で生成し（autoplay ポリシー対応）、再生中に
 * PUSH し直すと前回のソースを止めて即リトリガする（マシンガン連射）。
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { compositeTotalSec, scheduleComposite } from "../utils/renderStutter";
import {
  beatsToSeconds,
  phraseLengthSec,
  type RendaParams,
} from "../utils/stutterPlan";

const START_DELAY_SEC = 0.06;
/** 試聴 window: フレーズ前後に付ける元音源の長さ（小節数） */
const PREVIEW_ROLL_BARS = 1;

interface EngineConfig {
  buffer: AudioBuffer | null;
  params: RendaParams;
}

export interface StutterEngine {
  isPlaying: boolean;
  /** 合成タイムライン上の再生位置（秒）。停止中は null */
  playheadSec: number | null;
  play: () => void;
  stop: () => void;
}

export const useStutterEngine = (config: EngineConfig): StutterEngine => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playheadSec, setPlayheadSec] = useState<number | null>(null);

  const ctxRef = useRef<AudioContext | null>(null);
  const masterRef = useRef<GainNode | null>(null);
  const sourcesRef = useRef<AudioScheduledSourceNode[]>([]);
  const rafRef = useRef<number | null>(null);

  // PUSH 時点の最新パラメータを読むために ref へ写す
  const configRef = useRef(config);
  configRef.current = config;

  const disposePlayback = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    for (const src of sourcesRef.current) {
      try {
        src.stop();
      } catch {
        // 停止済み・未スケジュールは無視
      }
    }
    sourcesRef.current = [];
    masterRef.current?.disconnect();
    masterRef.current = null;
  }, []);

  const stop = useCallback(() => {
    disposePlayback();
    setIsPlaying(false);
    setPlayheadSec(null);
  }, [disposePlayback]);

  const play = useCallback(() => {
    const { buffer, params } = configRef.current;
    if (!buffer) return;

    if (!ctxRef.current) {
      ctxRef.current = new AudioContext();
    }
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") void ctx.resume();

    // リトリガ: 前回の再生を止めて撃ち直す
    disposePlayback();

    const master = ctx.createGain();
    master.connect(ctx.destination);
    masterRef.current = master;

    const barSec = beatsToSeconds(4, params.bpm);
    const phraseStartSec = params.startSec;
    const phraseEndSec =
      phraseStartSec + phraseLengthSec(params.lengthSixteenths, params.bpm);
    const totalSec = compositeTotalSec(buffer, params);
    const windowStartSec = Math.max(
      0,
      phraseStartSec - PREVIEW_ROLL_BARS * barSec,
    );
    const windowEndSec = Math.min(
      totalSec,
      phraseEndSec + PREVIEW_ROLL_BARS * barSec,
    );
    const timeOrigin = ctx.currentTime + START_DELAY_SEC;

    sourcesRef.current = scheduleComposite(ctx, master, buffer, params, {
      windowStartSec,
      windowEndSec,
      timeOrigin,
    });
    setIsPlaying(true);

    const updatePlayhead = () => {
      const posSec = windowStartSec + (ctx.currentTime - timeOrigin);
      if (posSec >= windowEndSec) {
        stop();
        return;
      }
      setPlayheadSec(Math.max(windowStartSec, posSec));
      rafRef.current = requestAnimationFrame(updatePlayhead);
    };
    rafRef.current = requestAnimationFrame(updatePlayhead);
  }, [disposePlayback, stop]);

  // アンマウント時に完全解放
  useEffect(() => {
    return () => {
      disposePlayback();
      void ctxRef.current?.close();
      ctxRef.current = null;
    };
  }, [disposePlayback]);

  return { isPlaying, playheadSec, play, stop };
};
